import React from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, ListChecks, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch as BaseSwitch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FeatureLocked } from '@/components/feature-locked';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { cn } from '@/lib/utils';
const MAX_QUESTIONS = 10;
const MAX_OPTIONS = 8;
const QUESTION_TYPES = ['short_text', 'paragraph', 'rating', 'choice'];
/** Generates a stable, server-safe id ([a-z0-9_]). The server keeps the id if
 *  it matches that charset, so the same question survives across edits. */
function makeQuestionId() {
    const source = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}`;
    return `q_${source
        .replace(/[^a-z0-9]/gi, '')
        .slice(0, 8)
        .toLowerCase()}`;
}
export function FeedbackQuestionsBuilder({ control, name, disabled, }) {
    const { t } = useTranslation('campaign');
    const { workspace } = useAuthenticationState();
    const allowed = workspace?.subscription?.plan?.allowCustomFeedbackQuestions === true;
    const { fields, append, remove, move } = useFieldArray({ control, name });
    if (!allowed) {
        return (<FeatureLocked plan="Solo" title={t('feedback_questions.locked_title')} description={t('feedback_questions.locked_desc')} upgradePath="/console/workspace/billing"/>);
    }
    const addQuestion = () => {
        if (fields.length >= MAX_QUESTIONS) {
            return;
        }
        const question = {
            id: makeQuestionId(),
            label: '',
            type: 'short_text',
            required: false,
        };
        // useFieldArray's value type is derived from the generic form — cast at
        // the single append boundary rather than threading T through callers.
        append(question);
    };
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4"/>
                    {t('feedback_questions.card_title')}
                </CardTitle>
                <CardDescription>{t('feedback_questions.card_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {fields.length === 0 && (<p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                        {t('feedback_questions.empty')}
                    </p>)}

                {fields.map((field, index) => (<QuestionRow key={field.id} control={control} name={name} index={index} disabled={disabled} isFirst={index === 0} isLast={index === fields.length - 1} onRemove={() => remove(index)} onMoveUp={() => index > 0 && move(index, index - 1)} onMoveDown={() => index < fields.length - 1 && move(index, index + 1)}/>))}

                <Button type="button" variant="outline" size="sm" disabled={disabled || fields.length >= MAX_QUESTIONS} onClick={addQuestion}>
                    <Plus className="h-3.5 w-3.5"/>
                    {t('feedback_questions.add')}
                </Button>
                {fields.length >= MAX_QUESTIONS && (<p className="text-xs text-muted-foreground">
                        {t('feedback_questions.max_reached', { max: MAX_QUESTIONS })}
                    </p>)}
            </CardContent>
        </Card>);
}
function QuestionRow({ control, name, index, disabled, isFirst, isLast, onRemove, onMoveUp, onMoveDown, }) {
    const { t } = useTranslation('campaign');
    const base = `${name}.${index}`;
    const typePath = `${base}.type`;
    const type = useWatch({ control, name: typePath });
    return (<div className="space-y-3 rounded-xl border bg-background p-3">
            <div className="flex items-start gap-2">
                <div className="flex-1">
                    <Controller control={control} name={`${base}.label`} render={({ field, fieldState }) => (<div className="space-y-1">
                                <Input {...field} value={field.value ?? ''} placeholder={t('feedback_questions.label_placeholder')} maxLength={120} disabled={disabled}/>
                                {fieldState.error?.message && (<p className="text-xs text-destructive">{fieldState.error.message}</p>)}
                            </div>)}/>
                </div>
                <div className="flex items-center gap-1">
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={disabled || isFirst} onClick={onMoveUp} aria-label={t('feedback_questions.move_up')}>
                        <ChevronUp className="h-4 w-4"/>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={disabled || isLast} onClick={onMoveDown} aria-label={t('feedback_questions.move_down')}>
                        <ChevronDown className="h-4 w-4"/>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" disabled={disabled} onClick={onRemove} aria-label={t('feedback_questions.remove')}>
                        <Trash2 className="h-4 w-4"/>
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <Controller control={control} name={typePath} render={({ field }) => (<Select value={field.value ?? 'short_text'} onValueChange={field.onChange} disabled={disabled}>
                            <SelectTrigger className="h-9 w-44">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {QUESTION_TYPES.map((value) => (<SelectItem key={value} value={value}>
                                        {t(`feedback_questions.type_${value}`)}
                                    </SelectItem>))}
                            </SelectContent>
                        </Select>)}/>

                <Controller control={control} name={`${base}.required`} render={({ field }) => (<label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                            <BaseSwitch checked={Boolean(field.value)} onCheckedChange={field.onChange} disabled={disabled}/>
                            {t('feedback_questions.required')}
                        </label>)}/>
            </div>

            {type === 'choice' && (<Controller control={control} name={`${base}.options`} render={({ field, fieldState }) => (<OptionsEditor value={field.value ?? []} onChange={field.onChange} disabled={disabled} error={fieldState.error?.message}/>)}/>)}

            {type === 'choice' && (<Controller control={control} name={`${base}.multiple`} render={({ field }) => (<label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                            <BaseSwitch checked={Boolean(field.value)} onCheckedChange={field.onChange} disabled={disabled}/>
                            {t('feedback_questions.allow_multiple')}
                        </label>)}/>)}
        </div>);
}
function OptionsEditor({ value, onChange, disabled, error }) {
    const { t } = useTranslation('campaign');
    const options = value.length > 0 ? value : [''];
    const update = (index, next) => {
        const copy = [...options];
        copy[index] = next;
        onChange(copy);
    };
    const removeOption = (index) => {
        const copy = options.filter((_, i) => i !== index);
        onChange(copy.length > 0 ? copy : ['']);
    };
    return (<div className="space-y-2 rounded-lg bg-muted/40 p-2">
            <span className="text-xs font-medium text-muted-foreground">{t('feedback_questions.options')}</span>
            {options.map((option, index) => (<div key={index} className="flex items-center gap-2">
                    <Input value={option} placeholder={t('feedback_questions.option_placeholder', { number: index + 1 })} maxLength={80} disabled={disabled} onChange={(event) => update(index, event.target.value)}/>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" disabled={disabled || options.length <= 1} onClick={() => removeOption(index)} aria-label={t('feedback_questions.remove_option')}>
                        <Trash2 className="h-4 w-4"/>
                    </Button>
                </div>))}
            {error && <p className="text-xs text-destructive">{error}</p>}
            <Button type="button" variant="outline" size="sm" disabled={disabled || options.length >= MAX_OPTIONS} onClick={() => onChange([...options, ''])}>
                <Plus className={cn('h-3.5 w-3.5')}/>
                {t('feedback_questions.add_option')}
            </Button>
        </div>);
}
